import { ResearchResponse } from '../types/fieldResearch';

interface FSFileHandle {
  getFile(): Promise<File>;
  createWritable(): Promise<FSWritableStream>;
}

interface FSWritableStream {
  write(data: string): Promise<void>;
  close(): Promise<void>;
}

let fileHandle: FSFileHandle | null = null;

// ── Download fallback ────────────────────────────────────────────────
const downloadJson = (dados: ResearchResponse[]): void => {
  const blob = new Blob([JSON.stringify(dados, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `field-research-responses-${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ── Selecionar arquivo (File System Access API ou input[file]) ───────
export const selecionarArquivo = async (): Promise<ResearchResponse[]> => {
  const win = window as any;

  if (win.showOpenFilePicker) {
    try {
      const [handle] = await win.showOpenFilePicker({
        types: [{ description: 'JSON', accept: { 'application/json': ['.json'] } }],
      });
      fileHandle = handle as FSFileHandle;
      const file = await fileHandle!.getFile();
      const text = await file.text();
      return JSON.parse(text) as ResearchResponse[];
    } catch {
      return [];
    }
  }

  // Fallback: input[type=file]
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return resolve([]);
      try {
        const text = await file.text();
        resolve(JSON.parse(text) as ResearchResponse[]);
      } catch {
        resolve([]);
      }
    };
    input.click();
  });
};

// ── Salvar arquivo ───────────────────────────────────────────────────
export const salvarArquivo = async (dados: ResearchResponse[]): Promise<void> => {
  const win = window as any;
  const json = JSON.stringify(dados, null, 2);

  // Tentar obter handle se não tiver
  if (!fileHandle && win.showSaveFilePicker) {
    try {
      fileHandle = (await win.showSaveFilePicker({
        suggestedName: 'field-research-responses.json',
        types: [{ description: 'JSON', accept: { 'application/json': ['.json'] } }],
      })) as FSFileHandle;
    } catch {
      downloadJson(dados);
      return;
    }
  }

  if (fileHandle) {
    try {
      const writable = await fileHandle.createWritable();
      await writable.write(json);
      await writable.close();
      return;
    } catch {
      /* fallthrough to download */
    }
  }

  downloadJson(dados);
};

// ── CRUD helpers ─────────────────────────────────────────────────────
export const adicionarRegistro = async (
  responses: ResearchResponse[],
  response: ResearchResponse,
): Promise<ResearchResponse[]> => {
  const updated = [...responses, response];
  await salvarArquivo(updated);
  return updated;
};

export const atualizarRegistro = async (
  responses: ResearchResponse[],
  response: ResearchResponse,
): Promise<ResearchResponse[]> => {
  const updated = responses.map((r) => (r.id === response.id ? response : r));
  await salvarArquivo(updated);
  return updated;
};

export const removerRegistro = async (
  responses: ResearchResponse[],
  id: string,
): Promise<ResearchResponse[]> => {
  const updated = responses.filter((r) => r.id !== id);
  await salvarArquivo(updated);
  return updated;
};

export const exportarJson = (dados: ResearchResponse[]): void => {
  downloadJson(dados);
};
